import * as TabsPrimitive from '@radix-ui/react-tabs';
import { type ComponentPropsWithoutRef, forwardRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { cn } from '../../lib/utils';

type TabsRootProps = ComponentPropsWithoutRef<typeof TabsPrimitive.Root> & {
  /**
   * URL search-param key used to remember the active tab across refresh
   * (and make it shareable as a deep link). Defaults to `"tab"`. Pass
   * `null` to opt out — useful for the rare case of two `<Tabs>` on the
   * same route, where the second one should use a distinct key.
   */
  persistKey?: string | null;
};

/**
 * Tabs primitive. By default the active tab is mirrored into the URL as
 * `?tab=…`, so refreshing or sharing the URL preserves the chosen tab.
 *
 * Behaviour:
 *  - Uncontrolled (no `value`/`onValueChange`): URL is the source of truth.
 *    Callers still pass `defaultValue` for the first-load fallback.
 *  - Controlled (caller passes `value` + `onValueChange`): URL persistence
 *    is bypassed entirely — the caller owns the state, we don't fight them.
 *  - Default tab → param removed (cleaner URLs); non-default → param set.
 *  - `replace: true` so each click doesn't push a history entry.
 */
export function Tabs({
  defaultValue,
  value,
  onValueChange,
  persistKey = 'tab',
  children,
  ...rest
}: TabsRootProps): JSX.Element {
  const [params, setParams] = useSearchParams();
  const isControlled = value !== undefined;

  const fromUrl = persistKey ? params.get(persistKey) : null;
  const effectiveValue = isControlled
    ? value
    : (fromUrl ?? defaultValue);

  const handleChange = (next: string) => {
    onValueChange?.(next);
    if (isControlled || !persistKey) return;
    const np = new URLSearchParams(params);
    if (!next || next === defaultValue) np.delete(persistKey);
    else np.set(persistKey, next);
    setParams(np, { replace: true });
  };

  return (
    <TabsPrimitive.Root value={effectiveValue} onValueChange={handleChange} {...rest}>
      {children}
    </TabsPrimitive.Root>
  );
}

export const TabsList = forwardRef<
  React.ComponentRef<typeof TabsPrimitive.List>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(function TabsList({ className, ...props }, ref) {
  return (
    <TabsPrimitive.List
      ref={ref}
      className={cn(
        'inline-flex h-10 items-center justify-start gap-1 rounded-md bg-surface-muted p-1 text-ink-muted',
        className,
      )}
      {...props}
    />
  );
});

export const TabsTrigger = forwardRef<
  React.ComponentRef<typeof TabsPrimitive.Trigger>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(function TabsTrigger({ className, ...props }, ref) {
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        'inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-surface data-[state=active]:text-ink data-[state=active]:shadow-card',
        className,
      )}
      {...props}
    />
  );
});

export const TabsContent = forwardRef<
  React.ComponentRef<typeof TabsPrimitive.Content>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(function TabsContent({ className, ...props }, ref) {
  return (
    <TabsPrimitive.Content
      ref={ref}
      className={cn(
        'mt-4 ring-offset-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30',
        className,
      )}
      {...props}
    />
  );
});

/**
 * Underline-style tab variants — flat sub-navigation pattern (Stripe /
 * Linear / GitHub). The list is a full-width horizontal bar with a
 * bottom hairline; the active trigger raises its own underline into
 * that hairline and shifts to the brand colour. Reads as proper
 * dashboard navigation rather than a segmented-control widget.
 *
 * Use [UnderlineTabsList] + [UnderlineTabsTrigger] together; they
 * compose with the standard `<Tabs>` and `<TabsContent>` primitives
 * unchanged.
 */
export const UnderlineTabsList = forwardRef<
  React.ComponentRef<typeof TabsPrimitive.List>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(function UnderlineTabsList({ className, ...props }, ref) {
  return (
    <TabsPrimitive.List
      ref={ref}
      className={cn(
        'flex h-10 items-end gap-1 border-b border-line text-ink-muted',
        className,
      )}
      {...props}
    />
  );
});

export const UnderlineTabsTrigger = forwardRef<
  React.ComponentRef<typeof TabsPrimitive.Trigger>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(function UnderlineTabsTrigger({ className, ...props }, ref) {
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        // -mb-px lifts the active underline 1px so it visually replaces
        // the list's bottom hairline rather than stacking with it.
        'relative -mb-px inline-flex items-center gap-1.5 whitespace-nowrap border-b-2 border-transparent px-3.5 pb-2.5 pt-2 text-[13px] font-medium transition-colors hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:border-brand data-[state=active]:text-brand',
        className,
      )}
      {...props}
    />
  );
});
